import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  CalendarDays, UserCheck, UserX, Clock, Home, AlertTriangle,
  ChevronLeft, ChevronRight, Save, Users, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { authService } from '../lib/auth';

const AttendanceMarking = () => {
  const user = authService.getCurrentUser();
  const [team, setTeam] = useState(null);
  const [isTeamLeader, setIsTeamLeader] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState({});
  const [existingAttendance, setExistingAttendance] = useState([]);
  const [statuses, setStatuses] = useState({});

  useEffect(() => {
    checkTeamLeaderStatus();
    fetchStatuses();
  }, []);

  useEffect(() => {
    if (team) {
      fetchDailyAttendance();
    }
  }, [selectedDate, team]);

  const checkTeamLeaderStatus = async () => {
    try {
      const res = await api.checkIsTeamLeader();
      setIsTeamLeader(res.data.is_team_leader);
      
      if (res.data.is_team_leader) {
        const teamRes = await api.getMyTeam();
        setTeam(teamRes.data);
      }
    } catch (error) {
      console.error('Error checking team leader status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatuses = async () => {
    try {
      const res = await api.getAttendanceStatuses();
      setStatuses(res.data);
    } catch (error) {
      console.error('Error fetching statuses:', error);
    }
  };

  const fetchDailyAttendance = async () => {
    if (!team) return;
    
    try {
      const res = await api.getDailyAttendance(selectedDate, team.id);
      setExistingAttendance(res.data);
      
      // Initialize attendance data from existing records
      const dataMap = {};
      res.data.forEach(record => {
        dataMap[record.employee_id] = {
          status: record.status,
          overtime_hours: record.overtime_hours,
          remarks: record.remarks
        };
      });
      setAttendanceData(dataMap);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const handleStatusChange = (employeeId, status) => {
    setAttendanceData(prev => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        status,
        overtime_hours: status === 'overtime' ? (prev[employeeId]?.overtime_hours || 1) : null
      }
    }));
  };

  const handleOvertimeHoursChange = (employeeId, hours) => {
    setAttendanceData(prev => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        overtime_hours: parseFloat(hours) || 0
      }
    }));
  };

  const handleRemarksChange = (employeeId, remarks) => {
    setAttendanceData(prev => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        remarks
      }
    }));
  };

  const saveAttendance = async () => {
    const attendanceList = Object.entries(attendanceData)
      .filter(([_, data]) => data.status)
      .map(([employeeId, data]) => ({
        employee_id: employeeId,
        date: selectedDate,
        status: data.status,
        overtime_hours: data.overtime_hours,
        remarks: data.remarks
      }));

    if (attendanceList.length === 0) {
      toast.error('Please mark attendance for at least one employee');
      return;
    }

    setSaving(true);
    try {
      const res = await api.markBulkAttendance(selectedDate, attendanceList);
      toast.success(`Attendance saved: ${res.data.success_count} records`);
      if (res.data.error_count > 0) {
        toast.warning(`${res.data.error_count} records had errors`);
      }
      fetchDailyAttendance();
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error(error.response?.data?.detail || 'Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const markAllPresent = () => {
    if (!team?.members) return;
    const newData = {};
    team.members.forEach(member => {
      newData[member.id] = { status: 'present' };
    });
    setAttendanceData(newData);
    toast.success('All marked as Present');
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];
  const isFuture = new Date(selectedDate) > new Date();

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present': return <UserCheck className="w-4 h-4 text-green-600" />;
      case 'absent': return <UserX className="w-4 h-4 text-red-600" />;
      case 'half_day': return <Clock className="w-4 h-4 text-amber-600" />;
      case 'work_from_home': return <Home className="w-4 h-4 text-blue-600" />;
      case 'overtime': return <Clock className="w-4 h-4 text-purple-600" />;
      default: return null;
    }
  };

  const getStatusButtonClass = (status, isSelected) => {
    const base = "px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1";
    if (!isSelected) return `${base} bg-slate-100 text-slate-600 hover:bg-slate-200`;
    
    switch (status) {
      case 'present': return `${base} bg-green-500 text-white`;
      case 'absent': return `${base} bg-red-500 text-white`;
      case 'half_day': return `${base} bg-amber-500 text-white`;
      case 'work_from_home': return `${base} bg-blue-500 text-white`;
      case 'overtime': return `${base} bg-purple-500 text-white`;
      default: return `${base} bg-slate-500 text-white`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isTeamLeader) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Not a Team Leader</h3>
            <p className="text-slate-500">You are not assigned as a team leader for any team.</p>
            <p className="text-slate-500 mt-2">Contact your HR administrator to be assigned as a team leader.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!team || !team.members || team.members.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <Users className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Team Members</h3>
            <p className="text-slate-500">Your team "{team?.team_name}" has no members assigned yet.</p>
            <p className="text-slate-500 mt-2">Contact HR to add members to your team.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const markedCount = Object.values(attendanceData).filter(d => d.status).length;
  const totalMembers = team.members.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mark Attendance</h1>
          <p className="text-slate-500">Team: <span className="font-medium">{team.team_name}</span></p>
        </div>
        
        {/* Date Navigation */}
        <div className="flex items-center gap-2 bg-white p-2 rounded-lg shadow-sm border">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => changeDate(-1)}
            data-testid="prev-date-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <div className="flex items-center gap-2 px-3">
            <CalendarDays className="w-5 h-5 text-indigo-600" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="border-0 focus:ring-0 font-medium text-slate-900"
              data-testid="attendance-date-input"
            />
            {isToday && (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Today</span>
            )}
          </div>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => changeDate(1)}
            disabled={isToday}
            data-testid="next-date-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {isFuture && (
        <div className="flex items-center gap-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <p className="text-amber-700">Cannot mark attendance for future dates</p>
        </div>
      )}

      {/* Summary Card */}
      <div className="flex gap-4 flex-wrap">
        <Card className="flex-1 min-w-[200px]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Marked</p>
                <p className="text-2xl font-bold text-slate-900">{markedCount} / {totalMembers}</p>
              </div>
              <div className={`p-3 rounded-full ${markedCount === totalMembers ? 'bg-green-100' : 'bg-slate-100'}`}>
                {markedCount === totalMembers ? (
                  <Check className="w-6 h-6 text-green-600" />
                ) : (
                  <Users className="w-6 h-6 text-slate-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={markAllPresent}
            disabled={isFuture}
            data-testid="mark-all-present-btn"
          >
            <UserCheck className="w-4 h-4 mr-2" />
            Mark All Present
          </Button>
          <Button 
            onClick={saveAttendance}
            disabled={saving || markedCount === 0 || isFuture}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-attendance-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Attendance'}
          </Button>
        </div>
      </div>

      {/* Attendance List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Team Members
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {team.members.map((member) => {
              const memberAttendance = attendanceData[member.id] || {};
              const existingRecord = existingAttendance.find(r => r.employee_id === member.id);
              
              return (
                <div 
                  key={member.id}
                  className={`p-4 rounded-lg border transition-all ${
                    memberAttendance.status ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-100'
                  }`}
                  data-testid={`member-row-${member.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Employee Info */}
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-indigo-700 font-medium">
                            {member.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {member.profile?.employee_name || member.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {member.profile?.employee_code || member.email}
                            {member.profile?.designation && ` • ${member.profile.designation}`}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Status Buttons */}
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(statuses).map(([key, value]) => (
                        <button
                          key={key}
                          onClick={() => handleStatusChange(member.id, key)}
                          disabled={isFuture}
                          className={getStatusButtonClass(key, memberAttendance.status === key)}
                          data-testid={`status-${key}-${member.id}`}
                        >
                          {getStatusIcon(key)}
                          {value.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Overtime Hours Input */}
                  {memberAttendance.status === 'overtime' && (
                    <div className="mt-3 flex items-center gap-2 pl-13">
                      <label className="text-sm text-slate-600">Overtime Hours:</label>
                      <input
                        type="number"
                        min="0.5"
                        step="0.5"
                        value={memberAttendance.overtime_hours || ''}
                        onChange={(e) => handleOvertimeHoursChange(member.id, e.target.value)}
                        className="w-20 px-2 py-1 border rounded text-sm"
                        data-testid={`overtime-hours-${member.id}`}
                      />
                    </div>
                  )}

                  {/* Existing Record Indicator */}
                  {existingRecord && (
                    <div className="mt-2 text-xs text-slate-500 pl-13">
                      Last marked by {existingRecord.marked_by_name} at{' '}
                      {new Date(existingRecord.marked_at).toLocaleString()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AttendanceMarking;
